/**
 * 지갑 화면의 판정 규칙 -- **순수 함수만** 둔다. React도 통신도 모른다.
 *
 * 파일을 줄이려고 뺀 게 아니라 규칙이 **한 군데**만 있어야 해서다. 지금은 wallet.page
 * 하나가 쓰지만, user.page 목록에 "정지된 지갑 있음" 뱃지를 붙이는 순간 같은 판정이
 * 복사된다. 복사된 판정은 한쪽만 고쳐지고 그 어긋남은 조용하다 -- 지금 wallet.page가
 * isStale을 인라인으로 들고 있는 게 그 씨앗이었다.
 *
 * == 상태를 하나로 접지 않는다 ==
 *
 * 세 축이 서로 독립이다.
 *
 *   배선(link)  -- 스냅샷이 오고 있나. 트레이더/브로커 쪽 사실
 *   운영(run)   -- wallets.status. 사람이 정한 운영 의도
 *   정지(halt)  -- wallets.halt_*. 장부와 실물이 어긋나 시스템이 멈춘 것
 *
 * 하나의 값으로 접으면 "정지 중인데 갱신도 멈춤"을 표현할 수 없고, 그 상태가 정확히
 * 사람이 가장 먼저 알아야 하는 상태다(요청을 넣어도 판정할 주체가 없다).
 */

/** 트레이더 발행 주기가 5초다. 그 6배를 넘겼으면 갱신이 멈춘 것으로 본다.
 *
 * 서버(BrokerController)는 낡은 스냅샷도 거르지 않고 그대로 보낸다 -- 조용히 빼면 웹이
 * "대상이 없다"와 "갱신이 멈췄다"를 구분할 수 없기 때문이다. 그 판단을 여기서 한다. */
export const STALE_MILLIS = 30000

/** uptAt은 스냅샷이 와야 바뀐다. 트레이더가 죽으면 아무것도 안 오므로 상태 변화도 리렌더도
 * 없어 **갱신 멈춤이 영영 안 뜬다.** 화면이 스스로 시계를 돌려 주기적으로 다시 판정한다. */
export const TICK_MILLIS = 5000

/** DB 재조회 주기. 트레이더 `refreshWalletControl`과 **같은 값**이어야 한다 -- 그보다 촘촘히
 * 물어봐야 새 판정이 있을 수 없고, 그보다 성기면 사람이 결과를 늦게 본다. */
export const POLL_MILLIS = 10000

/** 시세(`price`)가 이만큼 낡으면 흐리게 표시한다.
 *
 * 출처가 1분봉이라 정상 상태에서도 최대 1분 남짓 낡아 있다 -- 그건 지연이 아니라 이 값의
 * 성질이다. 봉이 **세 개** 빠졌다면 수집기가 밀린 것이고, 그건 사실이라 드러내야 한다
 * (`TfCandles.warnIfStale`이 트레이더 쪽에서 같은 판정을 로그로 남긴다). */
export const PRICE_STALE_MILLIS = 3 * 60 * 1000

/** 미실현 손익(`unrealizedAt`)이 이만큼 낡으면 흐리게 표시한다.
 *
 * **정상값이 하나로 안 정해진다.** 갱신 주체인 `scanVanishedPosition`이 1분 주기지만
 * non-STOP 미결이 살아 있으면 조기 리턴한다 -- 진입 지정가가 걸려 있으면 `entryTimeoutMillis`
 * (10분)까지 정상적으로 안 갱신된다.
 *
 * 그래서 이 임계는 "고장"이 아니라 **"슬슬 의심해볼 나이"**다. 진입 타임아웃의 절반으로 잡아,
 * 넘으면 값을 지우지 않고 흐리게만 한다 -- 지우면 사람이 "손익이 0이 됐다"로 읽는다. */
export const UNREALIZED_STALE_MILLIS = 5 * 60 * 1000

/** 배선 상태 -- 스냅샷이 오고 있는가. */
export const Link = {
  OK: 'OK',
  STALE: 'STALE',     // 스냅샷은 있는데 낡았다
  DOWN: 'DOWN',       // DB엔 있는데 스냅샷이 없다 = 미기동
  UNKNOWN: 'UNKNOWN', // 우리가 못 받는 중이라 판정을 보류한다
}

export const LinkLabel = {
  OK: '정상',
  STALE: '갱신 멈춤',
  DOWN: '미기동',
  UNKNOWN: '알 수 없음',
}

/** **고를 값의 이름**이다 -- 드롭다운 항목. 상태명이지 경과가 아니다.
 *
 * 셋이 같은 축이어야 한다: `가동` / `신규진입 정지` / `종료`. FIN만 진행형('종료 중')이면
 * "무엇을 고르는가"가 어긋난다 -- 사람이 고르는 건 도달할 상태다. */
export const RunLabel = {
  ACTIVE: '가동',
  NO_ENTRY: '신규진입 정지',
  FIN: '종료',
}

/** **지금 어떤 상태인가** -- 화면에 그릴 이름. `RunLabel`과 일부러 다르다.
 *
 * FIN에는 성격이 다른 두 구간이 있고, 그 차이가 사람의 행동을 바꾼다.
 *
 * {{{
 *   종료 중   아직 청산 주문이 나갈 수 있다(대개 1분 안). 지금 직접 거래하면 겹친다
 *   종료됨    손 뗐다(finSettled). 이후 트레이더가 이 지갑의 거래소를 안 본다
 * }}}
 *
 * 나머지 상태는 구간이 하나라 `RunLabel` 그대로다.
 *
 * ⚠ **`settled`는 스냅샷에서 온다.** 트레이더가 안 떠 있으면 스냅샷이 없어 `false`이고,
 * 그러면 여기서 "종료 중"이라고 말한다 -- 실제로는 아무것도 정리되고 있지 않다. 그 거짓말을
 * 여기서 막지 않는 이유는 **링크 뱃지가 이미 "미기동"을 말하고 있기 때문**이다. 두 축을
 * 각자 그리는 게 이 화면의 원칙이고(§상태를 하나로 접지 않는다), 여기서 겹쳐 판정하면
 * 그 원칙이 무너진다. */
export const runStateLabel = (run, settle) => {
  if (run !== 'FIN') {
    return RunLabel[run] || run
  }
  return settle.settled ? '종료됨' : '종료 중'
}

/**
 * 이 화면이 보여줄 `wallets.run_mode`.
 *
 * `LiveRunner.RunModes`는 `MAINNET | TESTNET | DRILL` 셋이고 DRILL도 정상적인 값이다 --
 * 별도 프로세스가 `DryRunExchangeAdapter`로 돌린다. 다만 **이 화면은 운영 화면이라**
 * 드릴 지갑까지 섞으면 실거래 지갑이 그 안에 묻힌다. 드릴 프로세스는 상시 가동이 아니라서
 * 대개 "미기동"으로만 줄줄이 뜨고, 그게 진짜 미기동을 눈에 안 띄게 만든다.
 *
 * `InitializationListener`가 dd 행을 시딩할 때 긋는 선과 **같은 선이다**(MAINNET/TESTNET).
 * 드릴 지갑을 봐야 할 화면이 생기면 그때 이 목록을 인자로 받으면 된다.
 */
export const LIVE_RUN_MODES = ['MAINNET', 'TESTNET']

/**
 * 총액에 **더할** `wallets.run_mode`. `LIVE_RUN_MODES`와 **일부러 다르다.**
 *
 * 두 질문이라서다.
 *
 *   목록  "어느 지갑이 지금 어떤 상태인가"  -> 테스트넷도 봐야 한다(가동 중인지 알아야 하니까)
 *   총액  "내 돈이 얼마인가"                -> 테스트넷은 내 돈이 아니다
 *
 * 상수 하나로 겸하면 둘 중 하나가 반드시 틀린다. 실제로 틀렸다 -- 실계좌 넷이 0.00인데
 * 테스트넷 4,273.39가 "확정 잔고 합"에 들어가 **계좌가 비었다는 사실이 화면에서 가려졌다.**
 *
 * `totalsOf`가 드릴을 거른 근거(`DryRunExchangeAdapter`의 가짜 잔고가 총액에 낀다)가 그대로
 * 걸린다. 테스트넷 잔고는 거래소가 실제로 돌려준 값이라 **출처는 다르지만 총자산이 아니라는
 * 점에서는 같다.**
 *
 * ⚠ 축이 섞이는 문제도 같이 닫힌다. 마스터(현물)는 `master_accounts`에 `run_mode`가 없고
 * **항상 MAINNET이다**(마스터관측 문서 §3 -- "그 전제는 컬럼이 아니라 합산 쿼리에 적는다").
 * 예전 총액은 `MAINNET+TESTNET 선물 + MAINNET 현물`이라 그 전제를 정면으로 어겼다.
 */
export const TOTAL_RUN_MODES = ['MAINNET']

/** 트레이더가 `rejectHaltClear`로 쓰는 접두사. **우리 코드가 만드는 문자열**이라 판별에 써도
 * 된다(LiveService.rejectHaltClear -> `s"clear rejected: $reason"`).
 *
 * 이걸로 가르는 이유: `halt_detail` 한 칸에 두 종류가 들어온다 -- 정지 자체의 사유와,
 * 해제 시도가 거절된 사유. 사람에게는 전혀 다른 소식이라 화면에서 갈라야 한다. */
const REJECT_PREFIX = 'clear rejected: '

/**
 * Scala `Option` 컬럼을 정규화한다.
 *
 * `OptionSerializer`(coin-common)가 두 가지를 한다.
 *
 *   None    -> `null`이 아니라 **빈 문자열** `""`
 *   Some(x) -> 값이 아니라 **문자열** (`context.serialize(o).getAsString`)
 *
 * 그래서 `halt_at`/`halt_clear_req_at` 같은 숫자 컬럼이 `"1754650000000"`으로 온다.
 * 그대로 비교하면 **사전순**이 된다 -- `"9" > "10"`이 참이다. 이 화면의 판정이 통째로
 * `halt_clear_req_at > halt_at` 하나에 걸려 있으므로 여기서 반드시 숫자로 만든다.
 *
 * `""`를 `??`로 못 거른다는 것도 같은 자리의 함정이다(빈 문자열은 nullish가 아니다).
 *
 * ⚠ 만약 응답에 `{"value":...}` 모양이 보이면 이 어댑터가 `Option[Long]` 같은 파라미터화
 * 타입에 안 붙은 것이다. 그건 여기서 받아낼 게 아니라 서버에서 고칠 일이다 -- 그때
 * 아래 함수들은 null을 돌려주고, 증상은 "정지는 보이는데 대기 표시가 안 뜬다"로 나온다.
 */
const optNum = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

const optStr = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  return String(v)
}

/**
 * 배선 판정.
 *
 * **연결이 없을 때 DOWN을 붙이지 않는 게 요점이다.** 브로커가 끊기면 전 지갑이 동시에
 * 소식을 못 받는데, 그때 지갑마다 "미기동"을 붙이면 거짓말이 된다 -- 트레이더는 멀쩡히
 * 돌고 있을 수 있고 못 받는 건 우리 쪽이다. 원인이 화면에 있는데 지갑을 의심하게 만든다.
 *
 * 다만 **신선한 스냅샷이 손에 있으면 연결 여부와 무관하게 OK다.** 30초 안에 온 값이
 * 있다는 것 자체가 "그 시점에 트레이더가 살아 있었다"는 사실이고, 그 사실은 지금 우리가
 * mqtt에 붙어 있는지와 상관없다. 이렇게 안 하면 페이지를 열 때마다 -- 첫 스냅샷은 /broker
 * 캐시로 먼저 오고 mqtt 접속은 그 뒤에 맺어진다 -- 전 지갑이 잠깐 "알 수 없음"으로 깜빡인다.
 */
export const linkOf = (snap, connected, nowMs) => {
  if (!snap) {
    return connected ? Link.DOWN : Link.UNKNOWN
  }
  if (nowMs - snap.uptAt > STALE_MILLIS) {
    return connected ? Link.STALE : Link.UNKNOWN
  }
  return Link.OK
}

/**
 * 정지 축 -- **DB 행에서만** 만든다. 스냅샷의 `halted`는 쓰지 않는다.
 *
 * 스냅샷이 5초로 더 빠른데도 DB를 정본으로 삼는 이유가 셋이다.
 *
 *  1. **거절 사유가 DB에만 있다.** `rejectHaltClear`가 `halt_detail`에 쓰고, 스냅샷에는
 *     그 칸이 없다. 요청을 냈는데 결과를 못 보면 사람은 10초마다 버튼을 다시 누른다.
 *  2. **판정 대기 여부도 DB에만 있다** -- `halt_clear_req_at > halt_at`.
 *  3. **미기동 지갑은 스냅샷이 아예 없다.** 정지된 채로 트레이더가 죽은 지갑이야말로
 *     사람이 봐야 하는데, 스냅샷 기준이면 그 지갑이 화면에서 사라진다.
 *
 * 두 출처를 섞지 않는다 -- 섞으면 "지금 이 값이 누구 것인가"를 아무도 말할 수 없게 된다
 * (LiveService의 halt 컬럼 소유권 주석이 경계하는 바로 그 모양이다). 스냅샷은 대신
 * **재조회 트리거**로만 쓴다(useWallets 참고).
 */
export const haltOf = (row) => {
  const code = optStr(row.haltCode)
  if (!code) {
    return { halted: false, pending: false, restartRequired: false }
  }

  // halt_at이 비어 있는 행은 원래 생길 수 없지만(updateHalt가 항상 같이 쓴다) 사람이 손으로
  // 만들 수 있다. 트레이더도 그때 0으로 보고 요청을 통과시키므로(handleHaltClearRequest)
  // 화면도 같은 규칙을 쓴다 -- 다르게 보면 화면과 실제 판정이 어긋난다.
  const at = optNum(row.haltAt) ?? 0
  const reqAt = optNum(row.haltClearReqAt)
  const detail = optStr(row.haltDetail) ?? ''
  const rejected = detail.startsWith(REJECT_PREFIX)

  return {
    halted: true,
    code,
    at,
    reqAt,
    detail,
    /** 아직 판정 안 한 요청. 거절되면 트레이더가 halt_at을 요청 시각 뒤로 밀어 이 값이 꺼진다. */
    pending: reqAt !== null && reqAt > at,
    /** 거절 사유(접두사 뗀 것). 없으면 null -- 정지 사유 원문은 detail에 그대로 있다. */
    rejectedReason: rejected ? detail.slice(REJECT_PREFIX.length) : null,
    /**
     * 눌러도 소용없는 거절인가.
     *
     * ⚠ 트레이더가 만든 **영어 문장을 부분 일치로 판별한다.** 문구가 바뀌면 조용히 false가
     * 되는데, 그 방향이 안전한 쪽이라 이렇게 둔다 -- 최악이 "헛 요청 한 번"이고, 그 비용은
     * 10초에 한 번 queryPosition(weight 5)이다. 반대로 판별을 강하게 걸었다가 오탐하면
     * 풀 수 있는 정지를 화면이 막아버린다.
     *
     * 근거 문자열: verifyHaltCleared -> `s"$code is not clearable while running -- restart required"`
     */
    restartRequired: rejected && detail.includes('restart required'),
  }
}

/**
 * 해제 요청을 지금 눌러도 되는가. 안 되면 이유를 같이 준다 -- 비활성 버튼만 있고 왜 그런지가
 * 없으면 사람은 화면이 고장 난 줄 안다.
 *
 * **미기동에서 막는 게 이 함수의 존재 이유다.** 정지 정보는 DB에 있으니 화면에 보이고
 * 버튼도 눌리고 요청도 DB에 잘 적힌다. 그런데 그걸 확인할 트레이더가 없어서 판정이 영원히
 * 안 나고, 화면은 "대기 중"에서 멈춘다. 눌리기 전에 말해주는 게 맞다.
 *
 * STALE은 **막지 않고 알리기만 한다.** 낡은 스냅샷은 트레이더가 죽은 것일 수도 있고
 * 브로커가 잠깐 끊긴 것일 수도 있어 단정할 수 없다. 단정할 수 없으면 사람이 정하게 둔다.
 */
export const canRequestHaltClear = (link, halt) => {
  if (!halt.halted) {
    return { ok: false, why: '' }
  }
  if (link === Link.DOWN) {
    return { ok: false, why: '트레이더가 떠야 처리됩니다 -- 지금 요청하면 판정 없이 대기만 합니다' }
  }
  if (link === Link.UNKNOWN) {
    return { ok: false, why: '연결 상태를 확인하는 중입니다' }
  }
  if (halt.pending) {
    return { ok: false, why: '판정 대기 중입니다 -- 최대 10초쯤 걸립니다' }
  }
  if (halt.restartRequired) {
    return { ok: false, why: '이 사유는 가동 중에 풀 수 없습니다 -- 재기동이 필요합니다' }
  }
  return { ok: true, why: '' }
}

/**
 * status를 이 값으로 바꾸면 무슨 일이 벌어지는가 -- **확인 창에 그대로 실을 문구**를 만든다.
 *
 * == 왜 확인을 받나 ==
 *
 * 셋이 드롭다운에 나란히 있으면 무게가 같아 보이는데 실제로는 다르다.
 *
 *   ACTIVE   -- 신규 진입을 다시 허용. 가역
 *   NO_ENTRY -- 신규 진입만 차단. 보유 포지션의 청산·스탑은 그대로 돈다. 가역
 *   FIN      -- **보유 포지션을 전량 시장가로 청산한다.** 되돌릴 수 없다
 *
 * 주인 지시로 셋 다 확인을 받되, 문구를 같게 두면 확인이 요식이 되어 정작 FIN에서 안 멈춘다.
 * 그래서 FIN만 `destructive`를 세우고 본문도 갈라 놓는다.
 *
 * == 시점을 같이 말하는 이유 ==
 *
 * FIN은 **누른 순간 청산되지 않는다.** 트레이더가 `refreshWalletControl`(10초)로 상태를 읽고
 * 그 다음 `scanWalletFin`(1분)에서 청산한다.
 *
 * **정지 중이어도 나간다**(2026-08). FIN은 halt를 무시한다 -- `scanWalletFin`이
 * `ignoreHalt`로 부르기 때문이다. 그 전까지는 정지가 청산을 막아서 "정지가 풀리는 즉시
 * 청산"이라고 안내했는데, 지금은 사실이 아니다.
 *
 * 안 나가는 경우는 **미기동 하나뿐**이고, 그건 "아무 일도 안 일어났네"로 보이는데 실은
 * **예약된 상태**다. 말 안 하면 사람이 잊어버리고 나중에 트레이더를 띄웠을 때 예상 못 한
 * 청산을 본다. 지연된 지뢰라 미리 말한다.
 *
 * == FIN이 건드리는 범위 ==
 *
 * **엔진이 낸 것뿐이다.** 우리 장부(미러/주문)에 없는 포지션은 손대지 않고, 정리가 끝나면
 * 그 지갑의 거래소 접근을 통째로 멈춘다(`finSettled`). 그래서 "이 뒤로 사람이 뭘 하든
 * 트레이더는 관여하지 않는다"까지 말해 준다 -- 청산만 알리면 사람은 지갑이 계속 감시되고
 * 있다고 생각한다.
 *
 * @param next    바꾸려는 status
 * @param link    `linkOf` 결과
 * @param halt    `haltOf` 결과
 * @param current 지금 status. FIN에서 돌아오는 경우에만 쓴다
 */
export const statusChangeIntent = (next, link, halt, current) => {
  const down = link === Link.DOWN
  const unknown = link === Link.UNKNOWN

  // 시점 한 줄 -- 세 상태 모두 같은 배선을 타므로 여기서 한 번만 만든다.
  const timing = () => {
    if (down) {
      return '지금은 트레이더가 떠 있지 않습니다 -- 값은 저장되고, 기동될 때 적용됩니다.'
    }
    if (unknown) {
      return '연결 상태를 확인하는 중입니다 -- 값은 저장되고, 트레이더가 최대 10초 안에 읽습니다.'
    }
    return '트레이더가 최대 10초 안에 읽습니다.'
  }

  if (next === 'FIN') {
    const lines = ['엔진이 낸 포지션과 미결 주문을 전부 종료합니다. 되돌릴 수 없습니다.']

    if (down) {
      // 예약 상태 -- "아무 일도 안 일어났다"로 보이지만 기동되면 바로 나간다.
      lines.push('지금은 트레이더가 떠 있지 않아 당장은 아무 일도 일어나지 않습니다. 기동되는 즉시 청산합니다.')
    }
    else if (halt.halted) {
      // 정지는 더 이상 청산을 막지 않는다. 그래도 한 줄 말해두는 이유: 사람이 "정지 중인데
      // 청산이 되나"를 확인 창에서 묻게 되고, 답이 없으면 취소를 누른다.
      lines.push('거래 정지 중이지만 종료는 그대로 진행됩니다 -- 정지가 청산을 막지 않습니다.')
    }
    else {
      lines.push('상태를 읽은 뒤 다음 종료 스캔에서 청산합니다 -- 대략 1분 안입니다.')
    }

    // 범위를 말해두지 않으면 "지갑을 계속 감시한다"고 오해한다. 이 뒤로는 트레이더가 이
    // 지갑의 거래소를 아예 안 본다 -- 사람이 직접 연 포지션도 마찬가지다.
    lines.push('종료 후에는 이 지갑의 거래를 더 이상 보지 않습니다 -- 직접 주문을 내거나 포지션을 열어도 트레이더는 관여하지 않습니다.')

    return {
      destructive: true,
      title: '전량 청산하고 종료할까요?',
      lines,
      confirmLabel: '청산하고 종료',
    }
  }

  if (next === 'NO_ENTRY') {
    return {
      destructive: false,
      title: '신규 진입을 막을까요?',
      lines: [
        '신규 진입과 증량만 막습니다. 보유 포지션의 청산·스탑은 그대로 돕니다.',
        timing(),
      ],
      confirmLabel: '신규진입 정지',
    }
  }

  if (next === 'ACTIVE') {
    const lines = ['신규 진입을 다시 허용합니다.', timing()]

    // FIN에서 돌아오는 건 **손 뗐던 지갑을 다시 잡는 것**이다. 그동안 사람이 연 포지션이
    // 남아 있으면 트레이더가 그걸 "설명 안 되는 포지션"으로 보고 곧바로 정지를 건다
    // (scanVanishedPosition -> POSITION_UNKNOWN). 겪고 나서 알면 사고로 읽힌다.
    if (current === 'FIN') {
      lines.push('종료 후 직접 연 포지션이 남아 있으면 트레이더가 그걸 설명할 수 없어 거래를 정지시킵니다 -- 먼저 정리해두세요.')
    }

    // 거래 정지는 별개 축이다. 여기서 안 풀린다는 걸 말해두지 않으면 "가동으로 바꿨는데
    // 왜 안 도나"가 된다 -- 해제 요청은 아래 정지 안내의 버튼이 따로 맡는다.
    if (halt.halted) {
      lines.push('거래 정지는 이걸로 풀리지 않습니다 -- 정지 해제는 따로 요청해야 합니다.')
    }
    return {
      destructive: false,
      title: '가동으로 되돌릴까요?',
      lines,
      confirmLabel: '가동',
    }
  }

  // 서버가 허용값을 늘렸는데 화면이 문구를 안 가진 경우. 막지 않고 일반 문구로 통과시킨다 --
  // 여기서 막으면 서버가 정본인데 화면이 거부하는 모양이 된다.
  return {
    destructive: false,
    title: `상태를 ${next}로 바꿀까요?`,
    lines: [timing()],
    confirmLabel: '변경',
  }
}

/**
 * DB 행(목록의 정본)에 스냅샷(실시간 값)을 덮는다.
 *
 * **목록이 DB에서 나오는 게 이 함수의 핵심이다.** 옛 화면은 목록까지 스냅샷에서 만들어서
 * 트레이더가 죽으면 지갑이 화면에서 통째로 사라졌다 -- "지갑 없음"과 "트레이더 미기동"이
 * 같은 화면이었다. 쏠 주체가 없다는 게 미기동의 정의라, 미기동은 스냅샷으로 표현할 수 없다.
 *
 * `orphans`는 반대 경우다 -- 스냅샷은 오는데 DB 행이 없다. 지갑을 지웠는데 트레이더가 아직
 * 그 지갑을 돌리고 있다는 뜻이라 **버리면 안 되는 사건이다.** 목록에서 조용히 빼면 실거래가
 * 도는 지갑이 아무 화면에도 안 나온다.
 *
 * **run_mode 필터는 목록에만 걸고 orphan 판정에는 안 건다.** 걸어버리면 드릴 프로세스가
 * 돌 때 그 스냅샷이 "DB에 없는 지갑"으로 잡혀 가짜 경보가 뜬다 -- 스냅샷에는 run_mode가
 * 실려 있지 않아 화면이 그걸 되받을 방법도 없다. 안 보여주는 것과 모르는 것은 다르다.
 *
 * @param rows      /wallet/user/{userId} 응답 (WalletsRow 배열)
 * @param snapsById useBroker의 records.wallet -- { 37: 스냅샷, 38: ... }
 * @param nowMs     판정 기준 시각. 인자로 받아야 이 함수가 순수해지고 테스트가 가능하다
 * @param runModes  목록에 남길 run_mode. 기본은 운영 지갑만
 */
export const mergeWallets = (rows, snapsById, connected, nowMs, runModes = LIVE_RUN_MODES) => {
  const snaps = snapsById || {}
  const seen = new Set()

  const all = (rows || []).map((row) => {
    const key = String(row.id)
    seen.add(key)
    const snap = snaps[key] || null
    return {
      walletId: row.id,
      row,
      snap,
      link: linkOf(snap, connected, nowMs),
      run: row.status,
      halt: haltOf(row),
    }
  })

  const wallets = all.filter((w) => runModes.includes(w.row.runMode))

  const orphans = Object.keys(snaps)
    .filter((key) => !seen.has(key))
    .map((key) => snaps[key])

  return { wallets, orphans }
}

/**
 * 심볼 시세와 진입가 대비 등락 -- **출처가 `klines`(수집기)인 값들.**
 *
 * `unrealizedOf`와 **묶지 않는 이유가 신선도다.** 저쪽은 거래소가 말한 시각(`unrealizedAt`)에
 * 매달려 있고 이쪽은 1분봉 시각(`priceAt`)에 매달려 있다. 한 덩어리로 묶어 "기준 시각 하나"로
 * 보여주면 둘 중 하나는 반드시 거짓말이 된다.
 *
 * `changePct`가 여기 있는 것도 그래서다 -- 트레이더가 `price`로 계산하므로 신선도가 `priceAt`을
 * 따른다. position 안에 실려 오지만 시각 축은 이쪽이다.
 *
 * `price`는 **포지션이 없어도 온다**(심볼의 사실이라 지갑 레벨에 실린다). `changePct`만
 * 포지션이 있어야 있다.
 */
export const marketOf = (snap, nowMs) => {
  const price = snap?.price ?? null
  const at = snap?.priceAt ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    has: price != null,
    price,
    /** 방향 보정된 등락률. **단위가 %다**(2.0 = 2%) -- 100을 다시 곱하면 안 된다.
     * 트레이더 `priceChangePct`가 `PositionCalc.priceChangeRatio × 100`으로 이미 만든 값이다. */
    changePct: snap?.position?.priceChangePct ?? null,
    at,
    ageMs,
    stale: ageMs != null && ageMs > PRICE_STALE_MILLIS,
  }
}

/**
 * 미실현 손익 -- **출처가 거래소인 값들.**
 *
 * 트레이더가 계산한 게 아니라 거래소 응답(`unRealizedProfit`/`markPrice`)을 통과시킨 값이다.
 * 그래서 거래소 앱에 뜨는 숫자와 맞는다.
 *
 * ⚠ **`marketOf().changePct`와 교차 검산하면 안 된다.** 기준가가 다르다 -- 이쪽은 mark price,
 * 저쪽은 최종 체결가(1분봉 종가)다. 부호는 같지만 값이 어긋나고, 움직임이 아주 작을 때는
 * 부호까지 갈릴 수 있다.
 *
 * `ratio`가 없을 수 있다 -- dd 기준잔고가 아직 없는 지갑이다(분모 없음). `pnl`은 있는데
 * `ratio`만 비는 게 정상 상태다.
 */
export const unrealizedOf = (snap, nowMs) => {
  const p = snap?.position
  const pnl = p?.unrealizedPnl ?? null
  const at = p?.unrealizedAt ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    has: pnl != null,
    pnl,
    ratio: p?.unrealizedRatio ?? null,
    /** 거래소가 손익을 계산한 기준가. 화면의 `price`(1분봉 종가)와 다른 값이다. */
    markPrice: p?.markPrice ?? null,
    at,
    ageMs,
    stale: ageMs != null && ageMs > UNREALIZED_STALE_MILLIS,
  }
}

/** 종료된 지갑의 거래소 관측값이 이만큼 낡으면 흐리게 표시한다.
 *
 * 출처는 `unrealizedOf`와 같은 `scanVanishedPosition`(1분 주기)인데 **임계가 다르다.**
 * 저쪽 5분은 미결 주문이 관측을 최대 10분까지 미룰 수 있어서 잡은 값이다. 종료된 지갑은
 * 우리 미결이 0인 게 `finSettled`의 전제라 그 지연이 원천적으로 없다 -- 1분 주기가 지켜지므로
 * 세 번 빠지면 조회가 실제로 실패하고 있는 것이다. */
export const OBSERVED_STALE_MILLIS = 3 * 60 * 1000

/**
 * 종료 상태와 거래소 실물 관측값.
 *
 * == "포지션 없음"이 두 뜻이라는 문제 ==
 *
 *   가동 중 -> 관측했고 없었다
 *   종료 후 -> 우리 미러가 없다 (거래소엔 있을 수 있다)
 *
 * 겉보기가 같아서 사람이 후자를 "비어 있다"로 읽는다. `settled`가 그 둘을 가른다.
 *
 * == `observed`는 `position`이 아니다 ==
 *
 * **엔진이 낸 게 아니다.** 종료 뒤 사람이 직접 연 포지션이 여기 잡힌다. 화면에서 반드시
 * 지갑 포지션과 다른 자리·다른 이름으로 그려야 한다 -- 같은 칸에 넣으면 FIN의 정의
 * ("엔진이 낸 것만 우리 것")를 화면이 뒤집는다.
 *
 * 서버가 종료된 지갑에서만 싣는다. 가동 중에는 미러가 정본이고, 둘을 나란히 두면 사람이
 * 대조하기 시작한다(`price`/`markPrice` 교차 검산 금지와 같은 이유).
 */
export const settlementOf = (snap, nowMs) => {
  const settled = Boolean(snap?.settled)
  const o = snap?.observed
  const at = o?.at ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    settled,
    /** 거래소에 실물이 있다. `settled`가 false면 항상 false다(서버가 안 싣는다). */
    has: Boolean(o),
    side: o?.side ?? null,
    qty: o?.qty ?? null,
    entryPrice: o?.entryPrice ?? null,
    markPrice: o?.markPrice ?? null,
    pnl: o?.unrealizedPnl ?? null,
    at,
    ageMs,
    stale: ageMs != null && ageMs > OBSERVED_STALE_MILLIS,
  }
}

/**
 * 확정 잔고 합 -- 서브 지갑(선물) + 마스터(현물).
 *
 * == 스냅샷을 안 쓴다 ==
 *
 * **`rows`(DB)만 더한다.** 스냅샷은 트레이더가 떠 있어야 오므로, 그걸 더하면 미기동 지갑이
 * 합계에서 통째로 빠져 **화면이 "돈이 사라졌다"로 보인다.** 지갑 카드가 미기동일 때 칩 하나가
 * 비는 것과 뿌리는 같지만, 이쪽은 **총액이 틀린다.**
 *
 * `wallets.balance`는 트레이더가 거래소를 부른 자리마다(기동·진입·청산·60분 스냅샷) 적어두는
 * 실잔고라, 트레이더가 지금 떠 있지 않아도 마지막 값이 남아 있다.
 *
 * == run_mode를 거른다 -- 목록과 **다른 선**이다 ==
 *
 * `DryRunExchangeAdapter.queryBalance`는 상수를 돌려주고 그 값이 `wallets.balance`에 실제로
 * 적재된다(러너의 `verify`가 DRILL도 통과시킨다). 안 거르면 **드릴 지갑의 가짜 돈이 총액에
 * 낀다.**
 *
 * ⚠ **테스트넷도 같은 이유로 뺀다**(2026-08-10). 출처는 다르지만(거래소가 실제로 돌려준 값)
 * 총자산이 아니라는 점에서 같다. 예전엔 목록과 같은 선(`LIVE_RUN_MODES`)을 써서
 * **실계좌 넷이 0.00인데 테스트넷 4,273.39가 "확정 잔고 합"에 들어갔다** -- 계좌가 비었다는
 * 사실이 화면에서 가려졌다. 지금은 `TOTAL_RUN_MODES`(그 상수 주석에 근거).
 *
 * **버리지는 않는다.** 테스트넷 몫은 따로 낸다 -- 청산·이체 시험 결과를 보려면 그 잔고도
 * 봐야 한다. 화면이 "합계에 안 들어감"을 글자로 적는 것이 짝이다(숫자만 따로 두면 눈으로 더한다).
 *
 * == 선물과 현물을 갈라 낸다 ==
 *
 * 서브는 USDM 선물 지갑이고 마스터는 현물이다. 같은 USDT라 더하는 건 맞지만 **화면이
 * "선물 합 + 현물"임을 드러내야 한다** -- 합쳐서 숫자 하나만 내면 어디에 얼마가 있는지가
 * 사라지고, `savingRatio`로 돈이 마스터에 쌓이는 게 안 보인다.
 *
 * == 신선도는 안 낸다 (주인 확정) ==
 *
 * `wallets.balance`에는 갱신 시각 컬럼이 없고, 있는 그대로 더한다. 지갑별 신선도는 카드가
 * 이미 말하고, 마스터는 자기 줄에 `regAt`을 단다.
 *
 * == 미실현은 안 들어간다 ==
 *
 * 그래서 이름이 "합산 잔고"가 아니라 **확정 잔고 합**이다. 포지션을 들고 있는 동안의
 * 평가손익은 이 숫자에 없다 -- 고장이 아니라 이 값의 성질이고, 화면이 그렇게 불러야 한다.
 *
 * @param rows   /wallet/user/{userId} 응답 (WalletsRow 배열)
 * @param master /ma/user/{userId} 응답 (`{ account, balance }`) -- 없으면 null
 */
export const totalsOf = (rows, master) => {
  const all = rows || []
  const live = all.filter((r) => TOTAL_RUN_MODES.includes(r.runMode))

  // `?? 0`으로 받는다 -- 옛 트레이더가 아직 그 칸을 안 쓰는 지갑은 컬럼 기본값 0이고,
  // 그건 "합계에 0을 보탠다"가 맞다. null로 두고 합계를 null로 만들면 지갑 하나 때문에
  // 총액이 통째로 사라진다.
  const sumBalance = (list) => list.reduce((sum, r) => sum + (r.balance ?? 0), 0)

  const futures = sumBalance(live)

  // 관측이 아직 없으면 마스터 몫은 없다 -- 0으로 세는 게 아니라 **모른다.** 그래도 총액은
  // 낸다(서브만으로도 뜻이 있다). 화면이 마스터 줄에서 "관측 대기"라고 말한다.
  const spot = master?.balance?.balance ?? null

  // 총액 밖 -- **목록에는 있고 합계에는 없는** 지갑들. `LIVE_RUN_MODES`에서 `TOTAL_RUN_MODES`를
  // 뺀 차집합이라 두 상수가 어긋나도 이 계산은 저절로 따라온다(둘을 각자 적으면 안 따라온다).
  const excludedModes = LIVE_RUN_MODES.filter((m) => !TOTAL_RUN_MODES.includes(m))
  const excluded = all.filter((r) => excludedModes.includes(r.runMode))

  return {
    futures,
    spot,
    total: futures + (spot ?? 0),
    /** 합계에 실제로 들어간 지갑 수. 목록과 다르면 run_mode 필터가 뭔가를 걸렀다는 뜻이다. */
    walletCount: live.length,
    /** 마스터 몫이 빠진 총액인가 -- 화면이 그걸 드러내야 한다. */
    spotMissing: spot == null,
    /**
     * 목록엔 뜨지만 **총액에 안 들어간** 몫(지금은 테스트넷).
     *
     * `null`이 아니라 `count: 0`으로 낸다 -- 화면이 "없음"과 "0원"을 구별해 그릴 수 있어야
     * 하고, 그 판정을 화면이 아니라 여기서 한다. 해당 지갑이 하나도 없으면 줄 자체를 안 그린다.
     */
    excluded: {
      modes: excludedModes,
      count: excluded.length,
      futures: sumBalance(excluded),
    },
  }
}

/** 스냅샷과 DB가 정지 여부를 다르게 말하고 있는가.
 *
 * **내용은 비교하지 않는다** -- 스냅샷의 `halted`는 사유 문자열이고 DB는 code/detail로
 * 갈려 있어 애초에 같은 모양이 아니다. "있다/없다"만 본다.
 *
 * 어긋나는 건 대개 시차다(스냅샷 5초 vs DB 폴링). 그 시차를 메우려고 DB를 다시 읽는 것이지,
 * 어긋남 자체를 사고로 보는 게 아니다. */
export const disagreesOnHalt = (view) =>
  view.wallets.some((w) => w.snap && Boolean(w.snap.halted) !== w.halt.halted)
